import {
  Component,
  Output,
  EventEmitter,
  TemplateRef,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewContainerRef,
  Renderer2,
  NgZone,
  AfterViewInit,
  inject,
  DestroyRef,
  input,
  computed,
  viewChild,
} from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { NgTemplateOutlet } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-panel',
  templateUrl: './ag1-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, Bind, Button, Tooltip, AngularSvgIconModule],
})
export class Ag1PanelComponent implements OnDestroy, AfterViewInit {
  private _destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private ngZone = inject(NgZone);
  private renderer = inject(Renderer2);

  // Controls the type of panel to be displayed
  readonly type = input<'overlay' | 'inline'>('overlay');
  // Label that appears on the panel header
  readonly label = input<string>('Panel');

  // Template the panel will be next to when it is open
  readonly wrappedFeatureTemplateRef = input<TemplateRef<any>>(undefined);

  // Template for the panel body
  readonly panelBodyTemplateRef = input<TemplateRef<any>>(undefined);

  // Template for the panel footer
  readonly panelFooterTemplateRef = input<TemplateRef<any>>(undefined);

  // computed signals
  readonly isOverlay = computed(() => this.type() === 'overlay');
  readonly isInline = computed(() => this.type() === 'inline');

  // Events
  @Output() closeEvent = new EventEmitter<void>();
  @Output() openEvent = new EventEmitter<void>();

  // We use ViewChild to get a reference to elements in the template
  readonly panelTemplate =
    viewChild.required<TemplateRef<any>>('panelTemplate');

  readonly featureContainer = viewChild<ElementRef>('featureContainer');
  readonly panelContainer = viewChild<ElementRef>('panelContainer');

  // configs
  panelWidth: number = 400;
  isOpen: boolean = false;
  isResizing: boolean = false;

  // Overlay, used to create the overlay Component
  private _overlayRef: OverlayRef | null = null;

  // resizing
  readonly PANEL_WIDTH_LOCAL_STORAGE_KEY = 'ag1PanelWidth';
  private readonly MIN_PANEL_WIDTH = 400;
  private readonly GAP_WIDTH = 12;

  constructor() {}

  ngOnDestroy(): void {
    this.close();
  }

  ngAfterViewInit(): void {
    const storedPanelWidth: string = localStorage.getItem(
      this.PANEL_WIDTH_LOCAL_STORAGE_KEY,
    );
    const panelContainer: ElementRef = this.panelContainer();

    if (storedPanelWidth) {
      this.panelWidth = Math.max(
        this.MIN_PANEL_WIDTH,
        parseFloat(storedPanelWidth),
      );
    } else if (panelContainer) {
      this.panelWidth = Math.max(
        this.MIN_PANEL_WIDTH,
        panelContainer.nativeElement.offsetWidth,
      );
    }

    if (this.isOpen && this.isInline()) {
      this.updateContainerWidths();
    }
    this.refreshInterface();
  }

  onSidePanelResize(event: MouseEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.panelWidth;
    this.isResizing = true;

    // use runOutsideAngular to avoid change detection in order to improve performance
    this.ngZone.runOutsideAngular(() => {
      // create observables for mousemove and mouseup events
      const mousemove$ = fromEvent<MouseEvent>(document, 'mousemove');
      const mouseup$ = fromEvent<MouseEvent>(document, 'mouseup');

      mousemove$
        .pipe(takeUntil(mouseup$), takeUntilDestroyed(this._destroyRef))
        .subscribe((moveEvent) => {
          const deltaX = startX - moveEvent.clientX;
          const newWidth = Math.max(
            this.MIN_PANEL_WIDTH,
            Math.min(
              startWidth + deltaX,
              window.innerWidth * 0.5 - this.GAP_WIDTH,
            ),
          );

          // trigger change detection in the Angular zone
          // this is necessary to update the view with the new width
          this.ngZone.run(() => {
            this.panelWidth = newWidth;
            this.updateContainerWidths();
            this.refreshInterface();
          });
        });

      mouseup$.pipe(take(1)).subscribe(() => {
        // trigger change detection in the Angular zone
        // this is necessary to update the view with the new width
        this.ngZone.run(() => {
          this.isResizing = false;
          this.refreshInterface();
          localStorage.setItem(
            this.PANEL_WIDTH_LOCAL_STORAGE_KEY,
            this.panelWidth.toString(),
          );
        });
      });
    });
  }

  private updateContainerWidths(): void {
    const panelContainer: ElementRef = this.panelContainer();

    if (panelContainer) {
      this.renderer.setStyle(
        panelContainer.nativeElement,
        'width',
        `${this.panelWidth}px`,
      );
    }

    const featureContainer: ElementRef = this.featureContainer();
    if (featureContainer && this.isInline()) {
      const totalWidth =
        featureContainer.nativeElement.parentElement.offsetWidth;
      const mainContainerWidth = totalWidth - this.panelWidth - this.GAP_WIDTH;
      this.renderer.setStyle(
        featureContainer.nativeElement,
        'width',
        `${mainContainerWidth}px`,
      );
    }
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    if (this.isOverlay()) {
      this.openOverlay();
    } else {
      const storedPanelWidth: string =
        localStorage.getItem(this.PANEL_WIDTH_LOCAL_STORAGE_KEY) || '400';

      if (storedPanelWidth) {
        this.panelWidth = Math.max(
          this.MIN_PANEL_WIDTH,
          parseFloat(storedPanelWidth),
        );
      }

      this.ngZone.run(() => {
        this.cdr.markForCheck();

        requestAnimationFrame(() => {
          this.updateContainerWidths();
          this.refreshInterface();
        });
      });
    }

    this.openEvent.emit();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.isOverlay() && this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    this.closeEvent.emit();
    this.refreshInterface();
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private openOverlay(): void {
    const positionStrategy = this.overlay
      .position()
      .global()
      .right('0')
      .top('0');

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      height: '100%',
    });

    this._overlayRef = this.overlay.create(overlayConfig);
    const portal = new TemplatePortal(
      this.panelTemplate(),
      this.viewContainerRef,
    );
    this._overlayRef.attach(portal);

    this._overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.close());

    this._overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          this.close();
        }
      });
  }
}
